import React from "react";
import { FiAlertTriangle } from "react-icons/fi";
import AuthContainer from "../components/AuthContainer";
import { getInviteErrorMessage } from "../components/User/inviteErrorMessages";

export type InviteErrorCode =
  | "INVITE_TOKEN_INVALID"
  | "INVITE_TOKEN_EXPIRED"
  | "VERIFICATION_LOCKED"
  | "RATE_LIMIT_EXCEEDED"
  | "PREFLIGHT_FAILED";

interface InviteErrorPageProps {
  code: InviteErrorCode;
  useAuthWrapper?: boolean;
  // Optional retry handle. When provided AND the code represents a
  // transient/recoverable failure, render a "Try again" button so the user
  // can recover without reloading.
  onRetry?: () => void;
}

const headings: Record<InviteErrorCode, string> = {
  INVITE_TOKEN_EXPIRED: "Your Invite Has Expired",
  INVITE_TOKEN_INVALID: "Invite Link Invalid",
  VERIFICATION_LOCKED: "Verification Temporarily Locked",
  RATE_LIMIT_EXCEEDED: "Please Wait a Moment",
  PREFLIGHT_FAILED: "Trouble Processing Your Invite",
};

const followups: Partial<Record<InviteErrorCode, string>> = {
  INVITE_TOKEN_EXPIRED: "Please contact your administrator to resend.",
  INVITE_TOKEN_INVALID: "Please contact your administrator to resend.",
  VERIFICATION_LOCKED: "You can try again shortly, or contact your administrator if this persists.",
  RATE_LIMIT_EXCEEDED: "Please wait a minute and refresh the page to try again.",
  PREFLIGHT_FAILED:
    "If the problem persists, please contact Procuracon support at support@procuracon.com.au.",
};

const RECOVERABLE_CODES: ReadonlyArray<InviteErrorCode> = ["PREFLIGHT_FAILED"];

export const InviteErrorPage: React.FC<InviteErrorPageProps> = ({ code, useAuthWrapper = true, onRetry }) => {
  const heading = headings[code];
  const message = getInviteErrorMessage(code);
  const followup = followups[code];
  const canRetry = Boolean(onRetry) && RECOVERABLE_CODES.includes(code);

  const errorDisplay = <><p data-testid="invite-error-message">{message}</p>
    {followup && (
      <p className="mt-6" data-testid="invite-error-followup">
        {followup}
      </p>
    )}
    {canRetry && (
      <button
        type="button"
        onClick={onRetry}
        data-testid="invite-error-retry"
        className="mt-6 inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
      >
        Try again
      </button>
    )}</>;

  return useAuthWrapper ? (
    <AuthContainer
      welcomeOverrideText=""
      additionalHeadingText={
        <div className="flex flex-row gap-4 mt-20">
          <FiAlertTriangle className="mb-4 h-10 w-10 fill-amber-400 text-black" />
          {heading}
        </div>
      }
    >
      {errorDisplay}
    </AuthContainer>
  ) : errorDisplay;
};
